import { Flex, Switch, theme, Form } from 'antd';
import PropTypes from 'prop-types';
import AntdFormItem from '../../../../../../../../../components/AntdFormItem';
import { useIntl } from 'react-intl';
import AntdSelect from '../../../../../../../../../components/AntdSelect';
import {
  CloseOutlined,
  InfoCircleFilled,
  PlusOutlined,
} from '@ant-design/icons';
import { useContext, useEffect, useState } from 'react';
import AntdInput from '../../../../../../../../../components/AntdInput';
import StructureTypeList from '../../../../utils/StructureTypeList';
import { useOutletContext } from 'react-router-dom';
import TagSelector from '../TagSelector';
import { NotificationsContext } from '../../../../../../../../../context/NotificationsContext';
import { getTagsByProject } from '../../../../../../../../../services/api/tags';

const BatchForm = ({
  form,
  onValuesChange,
  structuralElements,
  allowUnbatch,
  normative,
  deliveryNote,
  fetchingBatches,
  disableBatchSelector,
  availableBatches,
}) => {
  const intl = useIntl();
  const { token } = theme.useToken();
  const [isCertifiedDispersionChecked, setIsCertifiedDispersionChecked] =
    useState();
  const [controlType, setControlType] = useState();
  const { project } = useOutletContext() || {};
  const certifiedDis = form.getFieldValue('certified_dis');
  const [tags, setTags] = useState([]);
  const [fetchingTags, setFetchingTags] = useState(false);
  const { pushNotification } = useContext(NotificationsContext);
  const [batches, setBatches] = useState([]);

  useEffect(() => {
    if (project?.version === 2) {
      setFetchingTags(true);
      getTagsByProject(project.id_project)
        .then((res) => {
          setTags(res.data.data);
        })
        .catch(() => {
          pushNotification({
            type: 'error',
            message: intl.formatMessage({ id: 'tags.fetch_error' }),
          });
        })
        .finally(() => setFetchingTags(false));
    }
  }, [project, pushNotification, intl]);

  useEffect(() => {
    const concreteBatches = availableBatches || [];
    const batchCodeInForm = form.getFieldValue('batch_code');
    const excluded = [];
    const availableOnes = [];
    const controlTypeInForm = form.getFieldValue('control_type');
    const concreteQualityInForm = form.getFieldValue('concrete_quality');
    const structuralElementInForm = form.getFieldValue('structural_element');

    for (const element of concreteBatches) {
      const isCurrentBatch = deliveryNote?.concrete_batch === element.id;
      if (controlTypeInForm === '100%' && !isCurrentBatch) {
        excluded.push(element);
        continue;
      }
      if (
        controlTypeInForm === 'Estadístico' &&
        concreteQualityInForm === true
      ) {
        if (
          [
            'TIPO 2',
            'TIPO 3',
            'TIPO 4',
            'TIPO 5',
            'TIPO 6',
            'TIPO 7',
            'TIPO 10',
            'TIPO 11',
            'TIPO 12',
          ].includes(structuralElementInForm)
        ) {
          if (deliveryNote.mash_control) {
            if (
              element.delivery_note_data?.some((x) => x.mash_control === true)
            ) {
              excluded.push(element);
              continue;
            }
          }
        }
      }

      const alreadyIncluded = availableOnes.some(
        (y) =>
          element &&
          y.batch_code === element.batch_code &&
          !excluded.includes(element),
      );

      if (!alreadyIncluded) {
        availableOnes.push({
          ...element,
        });
      }
    }

    const isSelectedBatchValid = availableOnes.some(
      (batch) => batch.id === batchCodeInForm,
    );

    if (
      !isSelectedBatchValid &&
      batchCodeInForm !== 'unbatch' &&
      batchCodeInForm !== 'newBatch'
    ) {
      form.setFieldsValue({ batch_code: null });
    }

    setBatches(availableOnes);
  }, [availableBatches, deliveryNote, form]);

  useEffect(() => {
    setIsCertifiedDispersionChecked(
      form.getFieldValue('certified_dis') || false,
    );
    setControlType(form.getFieldValue('control_type'));
  }, [form, certifiedDis]);

  return (
    <Form form={form} layout="vertical" onValuesChange={onValuesChange}>
      <Flex gap={20} style={{ marginBottom: '1em' }}>
        <AntdFormItem
          label={intl.formatMessage({ id: 'control_type' })}
          name="control_type"
          style={{ flex: 1 }}
          rules={[
            {
              required: true,
              message: intl.formatMessage({ id: 'required' }),
            },
          ]}
        >
          <AntdSelect
            onChange={(value) => {
              setControlType(value);
              if (value === '100%') {
                form.setFieldsValue({
                  certified_dis: false,
                  delta: null,
                });
                setIsCertifiedDispersionChecked(false);
              }
            }}
            options={
              deliveryNote?.mash_control && !deliveryNote?.mash_control_extra
                ? [
                    {
                      label: intl.formatMessage({
                        id: 'control_type.statistic',
                      }),
                      value: 'Estadístico',
                    },
                    {
                      label: intl.formatMessage({ id: 'control_type.100%' }),
                      value: '100%',
                    },
                  ]
                : [
                    {
                      label: intl.formatMessage({
                        id: 'control_type.statistic',
                      }),
                      value: 'Estadístico',
                    },
                  ]
            }
          />
        </AntdFormItem>
        <AntdFormItem
          name="structural_element"
          style={{ flex: 1 }}
          label={intl.formatMessage({ id: 'structural_element' })}
          rules={[
            {
              required: true,
              message: intl.formatMessage({ id: 'required' }),
            },
          ]}
          tooltip={
            normative === 'coe'
              ? {
                  title: <StructureTypeList />,
                  styles: { body: { width: '40vw', padding: '2em 1em' } },
                  icon: (
                    <InfoCircleFilled
                      style={{ color: 'black', cursor: 'pointer' }}
                    />
                  ),
                }
              : undefined
          }
        >
          <AntdSelect
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: 'required',
                }),
              },
            ]}
            options={structuralElements?.map((element) => ({
              label: element.structural_element,
              value: element.structural_element,
            }))}
          />
        </AntdFormItem>
      </Flex>
      <Flex gap={20} style={{ marginBottom: '1em', flexWrap: 'wrap' }}>
        {project?.version === 2 && (
          <div style={{ width: 'calc(50% - 10px)' }}>
            <TagSelector
              form={form}
              project={project}
              disabled={disableBatchSelector}
              required={!!deliveryNote.concrete_batch}
              tags={tags}
              fetchingTags={fetchingTags}
              setTags={setTags}
            />
          </div>
        )}
        <div style={{ width: 'calc(50% - 10px)' }}>
          <AntdFormItem
            label={intl.formatMessage({ id: 'batch_code' })}
            name="batch_code"
            rules={
              deliveryNote.concrete_batch
                ? [
                    {
                      required: true,
                      message: intl.formatMessage({ id: 'required' }),
                    },
                  ]
                : undefined
            }
          >
            <AntdSelect
              disabled={disableBatchSelector}
              optionLabelProp="label"
              loading={fetchingBatches}
              placeholder={intl.formatMessage({
                id: 'batch_code.select.placeholder',
              })}
              options={[
                {
                  value: 'newBatch',
                  label: (
                    <Flex gap={5} align="center">
                      <PlusOutlined />{' '}
                      <strong>
                        {intl.formatMessage({
                          id: 'batch_code.select.newBatch',
                        })}
                      </strong>
                    </Flex>
                  ),
                },
                ...(allowUnbatch
                  ? [
                      {
                        value: 'unbatch',
                        label: (
                          <Flex gap={5} align="center">
                            <CloseOutlined
                              style={{ color: token.colorError }}
                            />{' '}
                            <strong style={{ color: token.colorError }}>
                              {intl.formatMessage({
                                id: 'batch_code.select.unbatch',
                              })}
                            </strong>
                          </Flex>
                        ),
                      },
                    ]
                  : []),
                ...batches?.map((batch) => ({
                  value: batch.id,
                  label: (
                    <>
                      {batch.tag_batch_data
                        ? `${batch.tag_batch_data.tag_data.name} ${batch.tag_batch_data.tag_number} --- `
                        : `${batch.batch_code} --- `}
                      <span
                        style={{ color: '#999' }}
                      >{`(${batch.current_batch_volume}%)`}</span>
                    </>
                  ),
                })),
              ]}
            />
          </AntdFormItem>
        </div>
        <div style={{ width: 'calc(50% - 10px)' }}>
          <Flex gap={10} wrap="wrap">
            <div style={{ width: 'calc(50% - 5px)' }}>
              <AntdFormItem
                label={intl.formatMessage({ id: 'concrete_quality' })}
                name="concrete_quality"
                valuePropName="checked"
                defaultValue={false}
              >
                <Switch
                  onChange={(checked) => {
                    if (checked && normative === 'coe') {
                      form.setFieldsValue({
                        certified_dis: false,
                        delta: null,
                      });
                      setIsCertifiedDispersionChecked(false);
                    }
                  }}
                />
              </AntdFormItem>
            </div>
            {normative === 'coe' && controlType !== '100%' && (
              <>
                <div style={{ width: 'calc(50% - 5px)' }}>
                  <AntdFormItem
                    label={intl.formatMessage({ id: 'certified_dis' })}
                    name="certified_dis"
                    valuePropName="checked"
                    defaultValue={false}
                  >
                    <Switch
                      onChange={(checked) => {
                        if (checked) {
                          form.setFieldsValue({
                            concrete_quality: false,
                            delta: null,
                          });
                        } else {
                          form.setFieldsValue({ delta: null });
                        }
                        setIsCertifiedDispersionChecked(checked);
                      }}
                    />
                  </AntdFormItem>
                </div>
                {isCertifiedDispersionChecked && (
                  <div style={{ width: 'calc(50% - 5px)' }}>
                    <AntdFormItem
                      label={intl.formatMessage({ id: 'delta' })}
                      name="delta"
                      rules={[
                        {
                          required: true,
                          message: intl.formatMessage({
                            id: 'required',
                          }),
                        },
                      ]}
                    >
                      <AntdInput size="large" type="number" />
                    </AntdFormItem>
                  </div>
                )}
              </>
            )}
          </Flex>
        </div>
      </Flex>
    </Form>
  );
};

BatchForm.propTypes = {
  form: PropTypes.object.isRequired,
  onValuesChange: PropTypes.func.isRequired,
  structuralElements: PropTypes.array.isRequired,
  allowUnbatch: PropTypes.bool.isRequired,
  normative: PropTypes.string.isRequired,
  deliveryNote: PropTypes.shape({
    mash_control_extra: PropTypes.bool,
    mash_control: PropTypes.bool,
    concrete_quality: PropTypes.bool,
    concrete_batch: PropTypes.number,
    id: PropTypes.number,
  }),
  fetchingBatches: PropTypes.bool,
  disableBatchSelector: PropTypes.bool.isRequired,
  availableBatches: PropTypes.array,
};

BatchForm.defaultProps = {
  deliveryNote: undefined,
  fetchingBatches: true,
  availableBatches: [],
};

export default BatchForm;
